import React from 'react';
import { motion } from 'framer-motion';
import { Star, MapPin, Quote } from 'lucide-react';
import { REVIEWS, getAggregateRating } from '../data/reviews';
import { Helmet } from 'react-helmet-async';

export default function ReviewsSection() {
  const agg = getAggregateRating();

  const aggregateSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "7/24 Lastik Yol Yardım",
    "url": "https://724lastik.com.tr",
    "telephone": "+905349687611",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": agg.ratingValue,
      "reviewCount": agg.reviewCount,
      "bestRating": agg.bestRating,
      "worstRating": agg.worstRating,
    },
    "review": REVIEWS.slice(0, 5).map(r => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": r.name },
      "datePublished": r.date,
      "reviewRating": { "@type": "Rating", "ratingValue": r.rating, "bestRating": 5 },
      "reviewBody": r.text,
    })),
  };

  return (
    <section
      data-testid="reviews-section"
      className="section"
      style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)' }}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(aggregateSchema)}</script>
      </Helmet>

      <div className="wrap">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div className="pill" style={{ marginBottom: '1rem' }}>Müşteri Yorumları</div>
          <h2 className="h2">
            Müşterilerimiz <span style={{ color: 'var(--green)' }}>Ne Diyor?</span>
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.5rem', marginTop: '.75rem' }}>
            <div style={{ display: 'flex', gap: '.125rem' }}>
              {[1,2,3,4,5].map(i => (
                <Star key={i} size={18} fill="#facc15" stroke="#facc15" />
              ))}
            </div>
            <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800, fontSize: '1.1rem', color: 'var(--text)' }}>
              {agg.ratingValue}
            </span>
            <span style={{ fontSize: '.8rem', color: 'var(--text2)' }}>
              ({agg.reviewCount} değerlendirme)
            </span>
          </div>
        </div>

        {/* Reviews Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1rem',
          maxWidth: 960,
          margin: '0 auto',
        }}>
          {REVIEWS.slice(0, 6).map((review, i) => (
            <motion.div
              key={review.id}
              data-testid={`review-card-${review.id}`}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: .35, delay: i * .06 }}
              style={{
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--rl)',
                padding: '1.375rem',
                transition: 'border-color .2s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--blue-border)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              {/* Stars */}
              <div style={{ display: 'flex', gap: '.1rem', marginBottom: '.75rem' }}>
                {Array.from({ length: review.rating }, (_, j) => (
                  <Star key={j} size={14} fill="#facc15" stroke="#facc15" />
                ))}
              </div>

              {/* Quote */}
              <div style={{ position: 'relative' }}>
                <Quote size={14} style={{
                  position: 'absolute', top: -2, left: -2,
                  color: 'var(--muted)', opacity: .3,
                }} />
                <p style={{
                  fontSize: '.85rem', color: 'var(--text2)', lineHeight: 1.7,
                  paddingLeft: '.25rem', marginBottom: '.875rem',
                }}>
                  {review.text}
                </p>
              </div>

              {/* Author */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                borderTop: '1px solid var(--border)', paddingTop: '.75rem',
              }}>
                <span style={{
                  fontFamily: "'Space Grotesk',sans-serif",
                  fontWeight: 700, fontSize: '.82rem', color: 'var(--text)',
                }}>
                  {review.name}
                </span>
                <span style={{
                  display: 'flex', alignItems: 'center', gap: '.25rem',
                  fontSize: '.72rem', color: 'var(--muted)',
                }}>
                  <MapPin size={11} /> {review.district}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
